import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import {
    AppBar,
    CssBaseline,
    ThemeProvider,
    Toolbar,
    styled,
} from "@mui/material";
import "Styles/highlights.css";
import theme from "Theme/AppTheme";
import EvalReview from "components/Agents/EvalReview/EvalReview";
import ClassifiersLayout from "components/Classifiers/ClassifiersLayout";
import Conversations from "components/Conversations/Conversations";
import AgentChatMenuIcon from "components/KBo/AgentChatMenuIcon";
import { AgentDrawer } from "components/KBo/AgentDrawer";
import AgentFullscreen from "components/KBo/Fullscreen/AgentFullscreen";
import KboSettingsLayout from "components/KBo/Settings/KboSettingsLayout";
import AgentStore from "components/KBo/Stores/AgentStore";
import ManagerInteractions from "components/ManagerInteractions/ManagerInteractions";
import MontageLibrary from "components/Montage/MontageLibrary";
import NotificationMenuIcon from "components/Notifications/NotificationMenuIcon";
import ImpersonationBanner from "components/Navigation/ImpersonationBanner";
import SignalsLayout from "components/Signals/SignalsLayout";
import Workflows from "components/Workflows/Workflows";
import { observer } from "mobx-react";
import { AppDomains } from "models/Permission/AppDomains";
import { useEffect, useMemo, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import logo from "../../authenticx-green.png";
import { LayoutDrawerStore } from "../../stores/Layout/LayoutDrawerStore";
import { useResizeObserver } from "../../utils/useResizeObserver";
import AgentCoaching from "../Agents/Coaching/AgentCoaching";
import AgentDashboard from "../Agents/Dashboard/AgentDashboard";
import AnalystDashboard from "../Dashboard/Analyst/AnalystDashboard";
import LandingDashboard from "../Dashboard/Landing/LandingDashboard";
import MessageStore from "../ManagerInteractions/Stores/MessageStore";
import AcxSnackBar from "../UI/AcxSnackBar";
import AcxDrawer from "../UI/Drawer/AcxDrawer";
import EvaluationSearch from "../UtilityComponents/EvaluationSearch";
import NavMenu, { ITab } from "./NavMenuNew";
import Page from "./Page";
import { MAIN_APP_BAR_HEIGHT_PX } from "./appBarHeight";
import ProfileMenuIcon from "./ProfileMenuIcon";
import { Routes as AppRoutes } from "./Routes";
import SamplerButton from "./SamplerButton";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";
import { navLinkProps } from "utils/useUnsavedChangesNavigationGuard";

const muiCache = createCache({
    key: "acx", // ← replaces seed/productionPrefix
    prepend: true, // ← equivalent to StylesProvider injectFirst
});

const WhiteAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.white.main,
    boxShadow:
        "0px 4px 1px -1px rgba(0,0,0,0.02), 0px 1px 1px 0px rgba(0,0,0,0.02), 0px 2px 6px 0px rgba(0,0,0,0.02)",
    height: MAIN_APP_BAR_HEIGHT_PX,
}));

const signalsLegacyRouteAliases = new Set(["reports", "reportsRv2"]);

function makeAppTabs(authStore: AuthStore): ITab[] {
    let agentsRoute = AppRoutes.AGENT_COACHING;

    if (
        authStore.isLoggedInUserAgent() &&
        authStore.orgStore.selectedOrganization &&
        authStore.permStore.agent
    )
        agentsRoute = AppRoutes.makeAgentDashboardRoute(
            authStore.orgStore.selectedOrganization.id,
            authStore.permStore.agent.id,
            authStore.permStore.agent.fullName,
        );

    // IMPORTANT NOTE - if a tab is to be hidden from the navbar, set hideInNavbar: true and put it AT THE END of the list.
    // This ensures that the tab order in the navbar remains consistent as well as highlighting of the active tabs
    return [
        {
            tabLabel: "Signals",
            route: "/signals",
            domain: AppDomains.Signals,
            newDomainName: "Signals",
        },
        {
            tabLabel: "Conversations",
            route: "/conversations",
            domain: AppDomains.Ultra,
            newDomainName: "Conversations",
        },
        {
            tabLabel: "My Evaluations",
            route: "/myevaluations",
            domain: AppDomains.MyEvaluations,
            newDomainName: "My Evaluations",
        },
        {
            tabLabel: "Workflows",
            route: AppRoutes.WORKFLOWS,
            domain: AppDomains.Workflows,
            newDomainName: "Workflows",
        },
        {
            tabLabel: "Montage Library",
            route: "/montagelibrary",
            domain: AppDomains.MontageLibrary,
            newDomainName: "Montage Library",
        },
        {
            tabLabel: "Interactions",
            route: "/interactions",
            domain: AppDomains.Interactions,
            newDomainName: "Interactions",
        },
        {
            tabLabel: "Agents",
            route: agentsRoute,
            domain: AppDomains.Agents,
            newDomainName: "Agent",
        },
        {
            tabLabel: "Classifiers",
            route: "/classifiers",
            domain: AppDomains.Classifiers,
            newDomainName: "Builder",
            // To View Classifiers Tab, user must have access to view the Classifier Builder
        },
        {
            tabLabel: "KBo",
            route: "/kbo",
            domain: AppDomains.KBo,
            newDomainName: "View KBo",
            hideInNavbar: true,
        },
    ];
}

const App = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const authStore = useStore(AuthStore);
    const drawerStore = useStore(LayoutDrawerStore);
    const messageStore = useStore(MessageStore);
    const agentStore = useStore(AgentStore);
    const [activeTab, setActiveTab] = useState<number>(0);
    const signalsPageStore = useStore(SignalsPageStore);

    //prefix for all routes on this page
    const routePrefix = "/app";
    const isOnKboRoute = location.pathname.startsWith(`${routePrefix}/kbo`);
    const isWaitingForKboAccessCheck =
        isOnKboRoute &&
        !agentStore.hasViewKboPermission &&
        !agentStore.hasResolvedKboStatus;

    //List of tabs, filtered accordingly by authorization
    const tabs: ITab[] = useMemo(() => {
        return makeAppTabs(authStore).filter((tab) => {
            //Non-Ultra Agent Users only have access to agent coaching tab
            if (authStore.isLoggedInUserAgent() && !authStore.isUserUltra()) {
                return tab.route.startsWith(AppRoutes.AGENT_COACHING);
            }
            //Non-Agent users always have acess to My Dashboard tab, Ultra users always have access to all tabs
            else if (
                tab.domain === AppDomains.MyDashboard ||
                authStore.isUserUltra()
            ) {
                return true;
            } else if (tab.domain === AppDomains.KBo) {
                return agentStore.canAccessKboRoutes;
            }
            //Non-Agent Non-Ultra users V2 Permissions check for all tabs besides dashboard
            else {
                return (
                    tab.newDomainName &&
                    authStore.canUserView(tab.newDomainName)
                );
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        agentStore.canAccessKboRoutes,
        authStore,
        authStore.homePageStore.homePageRoute,
    ]);

    //Function on tab change
    const updateTab = (index: number, shouldNav: boolean = true) => {
        setActiveTab(index);
        if (shouldNav) navigate(routePrefix + tabs[index].route!);
    };

    //Handle special navigation scenarios
    useEffect(() => {
        //If the user isn't authenticated, force them back to login url
        if (!authStore.permStore || !authStore._isAuthenticated) {
            navigate(authStore.getLoginUrl());
            return;
        }

        //If the user is on the dashboard tab, and they have access to it, let them be
        if (
            location.pathname === routePrefix &&
            tabs.some((tab) => tab.domain === AppDomains.MyDashboard) &&
            activeTab === 0
        ) {
            return;
        }

        if (isWaitingForKboAccessCheck) {
            return;
        }

        const subroutes = location.pathname.split("/");
        const targetSubroute = signalsLegacyRouteAliases.has(subroutes[2])
            ? "signals"
            : subroutes[2];
        const currentTabIndex = tabs.findIndex((tab) => {
            const tabRouteParts = tab.route.substring(1).split("/");
            return tabRouteParts[0] === targetSubroute;
        });

        //If the route the user is on isn't in their array of tabs, navigate them to the first tab (dashboard or agent dash).
        if (currentTabIndex === -1) {
            updateTab(0);
            return;
        }

        //Otherwise, just make sure the active tab reflects the route the user is on.
        updateTab(currentTabIndex, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWaitingForKboAccessCheck, location.pathname]);

    const tabsForNavbar = tabs.filter((tab) => !tab.hideInNavbar);

    const hideEvalSearch =
        authStore.isLoggedInUserAgent() && !authStore.isUserUltra();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { elementHeight: appBarHeight, theRef } =
        useResizeObserver<HTMLElement>({
            name: "MainAppNavToolbar",
            pixelDelta: 1,
            debounceWait: 50,
        });

    return (
        <>
            <CssBaseline />
            <AcxSnackBar
                open={messageStore.showMessage}
                onClose={messageStore.showNextMessage}
                message={messageStore.currentMessage?.text || ""}
                severity={messageStore.currentMessage?.severity}
            />
            <WhiteAppBar
                ref={theRef}
                style={{
                    boxShadow:
                        "0px 4px 1px -1px rgba(0,0,0,0.02), 0px 1px 1px 0px rgba(0,0,0,0.02), 0px 2px 6px 0px rgba(0,0,0,0.02)",
                    display: signalsPageStore.currentDashboard
                        ?.isPresentationMode
                        ? "none"
                        : "inherit",
                }}
            >
                <Toolbar sx={{ height: "100%", display: "flex" }}>
                    <img
                        alt=""
                        src={logo}
                        style={{
                            height: 30,
                            cursor: "pointer",
                        }}
                        onClick={() => navigate("/")}
                        // Navigates imperatively (no <a>) — lets an unsaved-changes guard find this click.
                        {...navLinkProps}
                    />

                    <NavMenu
                        onChange={updateTab}
                        selected={activeTab}
                        tabs={tabsForNavbar}
                    />

                    {!hideEvalSearch && <EvaluationSearch />}

                    <AgentChatMenuIcon />

                    <SamplerButton showIcon={authStore.canUserViewAdminApp()} />

                    <NotificationMenuIcon drawerOffsetPx={appBarHeight} />

                    <ProfileMenuIcon
                        currLocation={"mainApp"}
                        organizationName={
                            authStore.orgStore.selectedOrganization?.name ?? ""
                        }
                    />
                </Toolbar>
            </WhiteAppBar>
            <div
                style={{
                    minHeight: `${appBarHeight || MAIN_APP_BAR_HEIGHT_PX}px`,
                }}
            />
            <ImpersonationBanner />
            <div>
                <AgentDrawer />
                <AcxDrawer
                    customRootStyles={drawerStore.customRootStyles}
                    anchor={drawerStore.anchor}
                    open={drawerStore.isOpen}
                    collapsible={drawerStore.collapseSize}
                    variant={drawerStore.variant}
                    offsetPx={drawerStore.offsetPx}
                    offsetPosition={drawerStore.offsetPosition}
                    transitionDuration={drawerStore.transitionDuration}
                    onDrawerClose={() => drawerStore.setOpen(false)}
                    drawerStore={drawerStore}
                    onDrawerOpen={() => drawerStore.setOpen(true)}
                    content={drawerStore.contentInjector?.()}
                />
            </div>
            <Routes>
                <Route
                    path="myevaluations"
                    element={
                        <Page title="My Evaluations | Authenticx">
                            <AnalystDashboard />
                        </Page>
                    }
                ></Route>
                <Route
                    path="montagelibrary"
                    element={
                        <Page title="Montage Library | Authenticx">
                            <MontageLibrary />
                        </Page>
                    }
                ></Route>

                <Route path={AppRoutes.EVALUATION}></Route>
                <Route
                    path="classifiers/*"
                    element={
                        <Page title="Classifiers | Authenticx">
                            <ClassifiersLayout />
                        </Page>
                    }
                ></Route>
                <Route
                    path="interactions/*"
                    element={<ManagerInteractions />}
                ></Route>
                <Route
                    path={"conversations/*"}
                    element={
                        <Page title="Conversations | Authenticx">
                            <Conversations />
                        </Page>
                    }
                ></Route>
                <Route
                    path={"agentcoaching"}
                    element={
                        <Page title="Agents | Authenticx">
                            <AgentCoaching />
                        </Page>
                    }
                ></Route>
                <Route
                    path="agentcoaching/evaluationreview"
                    element={
                        <Page title="Evaluation Review | Authenticx">
                            <EvalReview />
                        </Page>
                    }
                ></Route>
                <Route
                    path={"agentcoaching/dashboard/:orgId/:agentId/:agentName"}
                    element={<AgentDashboard />}
                ></Route>
                <Route path={"workflows/*"} element={<Workflows />}></Route>
                <Route
                    path="kbo/chats"
                    element={
                        <Page title="KBo | Authenticx">
                            <AgentFullscreen />
                        </Page>
                    }
                ></Route>
                <Route
                    path="kbo/chats/:chatId"
                    element={
                        <Page title="KBo | Authenticx">
                            <AgentFullscreen />
                        </Page>
                    }
                ></Route>
                <Route
                    path="kbo/settings/*"
                    element={
                        <Page title="KBo Settings | Authenticx">
                            <KboSettingsLayout />
                        </Page>
                    }
                ></Route>
                <Route
                    path="reports/*"
                    element={
                        <Page title="Signals | Authenticx">
                            <SignalsLayout />
                        </Page>
                    }
                />
                <Route
                    path="reportsRv2/*"
                    element={
                        <Page title="Signals | Authenticx">
                            <SignalsLayout />
                        </Page>
                    }
                />
                <Route
                    path="signals/:dashboardRoute" // Load main dashboard of type
                    element={
                        <Page title="Signals | Authenticx">
                            <SignalsLayout />
                        </Page>
                    }
                />
                <Route
                    path="signals/:dashboardRoute/:dashboardId" // Load specific dashboard
                    element={
                        <Page title="Signals | Authenticx">
                            <SignalsLayout />
                        </Page>
                    }
                />
                <Route
                    path="signals/home" // Load home page
                    element={
                        <Page title="Signals | Authenticx">
                            <SignalsLayout />
                        </Page>
                    }
                />
                <Route
                    path="signals/*" // Fallback route
                    element={
                        <Page title="Signals | Authenticx">
                            <SignalsLayout />
                        </Page>
                    }
                />
                <Route
                    path="/"
                    element={
                        <Page title="Dashboard | Authenticx">
                            <LandingDashboard isSignals={false} />
                        </Page>
                    }
                ></Route>
            </Routes>
        </>
    );
});

export default function Layout() {
    // console.log("layout component is executing");
    return (
        <CacheProvider value={muiCache}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <App />
            </ThemeProvider>
        </CacheProvider>
    );
}
